import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import getReferralData from "@Pages/ProfileRefer/helpers/getReferralData/getReferralData";
import { logger } from "helpers/logger";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./ReferralsTable.module.scss";
import { shortMonthsArray } from "@Components/DatePicker/helper";
import Tooltip from "@Components/Tooltip/Tooltip";
import InfoIconV2 from "@Icons/InfoIconV2";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

interface ReferralData {
  name: string;
  referDate: string;
  bookingStatus: string;
  rewardStatus: string;
  id: string;
}

const ReferralsTable = () => {
  const [referralsData, setReferralsData] = useState<ReferralData[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { isUserLoggedIn, userData } = useSelector(({ appData }: AppState) => ({
    isUserLoggedIn: appData.isUserLoggedIn,
    userData: appData.userData,
  }));

  const useruid = userData?.data?.id;

  const fetchReferralData = async (userId: string) => {
    if (userId) {
      try {
        const res = await getReferralData(userId, {});
        const rawData = res?.data?.result || [];

        const transformedData: ReferralData[] = rawData.map((item: any) => ({
          id: item.id?.toString() || "",
          name: item.user_referral_details?.user_name || "Unnamed",
          referDate: item.created_at || "",
          bookingStatus: item.user_referral_details?.booking_status || "N/A",
          rewardStatus: item.user_referral_details?.cashback_status || "N/A",
        }));

        setReferralsData(transformedData);
      } catch (err) {
        logger.error(err);
      }
    }
  };

  const formatReferralDate = (val: string): string => {
    const date = new Date(val);
    const day = date.getDate();
    const month = t(shortMonthsArray[date.getMonth()]);
    const year = date.getFullYear();

    const getOrdinal = (n: number) => {
      if (n > 3 && n < 21) return "th";
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getOrdinal(day)} ${month}, ${year}`; // e.g. 4th Oct, 2024
  };

  const toggleRowExpansion = (id: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const getInitials = (name: string | undefined | null): string => {
    if (!name || typeof name !== "string") return "NA";

    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (index: number): string => {
    const colors = [
      "referralsTableAvatarGreen",
      "referralsTableAvatarPurple",
      "referralsTableAvatarBlue",
      "referralsTableAvatarTeal",
    ];
    return colors[index % colors.length];
  };
  const getStatusColor = (status: string | undefined | null): string => {
    if (!status || typeof status !== "string") return styles.referralsTableStatusWarning;

    switch (status.trim().toLowerCase()) {
      case "booking completed":
      case "disbursed":
        return styles.referralsTableStatusSuccess; // usually green
      case "cancelled":
      case "did not book":
        return styles.referralsTableStatusError; // usually red
      case "pending":
      case "in progress":
        return styles.referralsTableStatusWarning; // usually yellow
      default:
        return styles.referralsTableStatusWarning;
    }
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      fetchReferralData(useruid);
    }
  }, [isUserLoggedIn, useruid]);

  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();

  if (!isUserLoggedIn) {
    return <></>;
  }

  return (
    <div
      className={formatClassNames(
        styles.referralsTableContainer,
        !isDesktop ? styles.referralsTableContainer : "",
      )}
    >
      {isUserLoggedIn && (
        <div className={formatClassNames(styles.loggedInGap, !isDesktop ? styles.mobile : "")} />
      )}
      <div className={styles.referralsTableCard}>
        <h2 className={styles.referralsTableTitle}>{t("referral.table.title")}</h2>

        {referralsData.length === 0 ? (
          <div className={styles.referralsTableEmptyState}>
            <p className={styles.referralsTableEmptyText}>
              {t("referral.table.emptyState")}
              <span className={styles.referralsTableRewardAmount}> £50</span>
            </p>
          </div>
        ) : (
          <div className={styles.referralsTableWrapper}>
            {/* Desktop Header */}
            <div className={styles.referralsTableDesktopHeader}>
              <div className={styles.referralsTableHeaderCell}>{t("referral.table.name")}</div>
              <div className={styles.referralsTableHeaderCell}>{t("referral.table.referDate")}</div>
              <div className={styles.referralsTableHeaderCell}>
                {t("referral.table.bookingStatus")}
              </div>
              <div className={styles.referralsTableHeaderCell}>
                {t("referral.table.rewardStatus")}
              </div>
            </div>

            {/* Mobile Header */}
            <div className={styles.referralsTableMobileHeader}>
              <div className={styles.referralsTableHeaderCell}>{t("referral.table.name")}</div>
              <div className={styles.referralsTableHeaderCell}>{t("referral.table.referDate")}</div>
            </div>

            {/* Rows */}
            {referralsData.map((referral, index) => (
              <div key={referral.id || index} className={styles.referralsTableRow}>
                <div className={styles.referralsTableRowContent}>
                  <div className={styles.referralsTableNameSection}>
                    <div
                      className={`${styles.referralsTableAvatar} ${styles[getAvatarColor(index)]}`}
                    >
                      {getInitials(referral.name)}
                    </div>
                    <span className={styles.referralsTableName}>{referral.name}</span>
                  </div>

                  <div className={styles.referralsTableDateSection}>
                    <span className={styles.referralsTableDate}>
                      {" "}
                      {formatReferralDate(referral.referDate)}
                    </span>

                    <button
                      className={styles.referralsTableExpandButton}
                      onClick={() => toggleRowExpansion(referral.id || index.toString())}
                      aria-expanded={expandedRows.has(referral.id || index.toString())}
                      type="button"
                      aria-label={
                        expandedRows.has(referral.id || index.toString())
                          ? "Collapse row"
                          : "Expand row"
                      }
                    >
                      <svg
                        className={`${styles.referralsTableChevron} ${
                          expandedRows.has(referral.id || index.toString())
                            ? styles.referralsTableChevronExpanded
                            : ""
                        }`}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Desktop Status Columns */}
                  <div className={styles.referralsTableDesktopStatus}>
                    <div className={styles.referralsTableStatusCell}>
                      <span
                        className={`${styles.referralsTableStatusBadge} ${getStatusColor(
                          referral.bookingStatus,
                        )}`}
                      >
                        {referral.bookingStatus}
                      </span>
                    </div>
                    <div className={styles.referralsTableStatusCell}>
                      <span
                        className={`${styles.referralsTableStatusBadge} ${getStatusColor(
                          referral.rewardStatus,
                        )} `}
                      >
                        {referral.rewardStatus}
                      </span>
                      <Tooltip
                        content={t("referral.table.tooltip")}
                        direction="bottom"
                        className={styles.tooltipProfileRefer}
                        hideOnClick
                        withTip
                      >
                        <InfoIconV2 height="14" width="14" />
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Mobile Expanded Content */}
                {expandedRows.has(referral.id || index.toString()) && (
                  <div className={styles.referralsTableExpandedContent}>
                    <div className={styles.referralsTableStatusRow}>
                      <span className={styles.referralsTableStatusLabel}>
                        {t("referral.table.bookingStatus")}:
                      </span>
                      <span
                        className={`${styles.referralsTableStatusBadge} ${
                          styles[getStatusColor(referral.bookingStatus)]
                        }`}
                      >
                        {referral.bookingStatus}
                      </span>
                    </div>
                    <div className={styles.referralsTableStatusRow}>
                      <span className={styles.referralsTableStatusLabel}>
                        {t("referral.table.rewardStatus")}:
                      </span>
                      <span
                        className={`${styles.referralsTableStatusBadge} ${
                          styles[getStatusColor(referral.rewardStatus)]
                        }`}
                      >
                        {referral.rewardStatus}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ReferralsTable, {
  componentName: "ReferralsTable-ReferralDesktop",
  showForChina: true,
});
